import type { ModuleFederationRuntimePlugin } from '@module-federation/enhanced/runtime'

// MF1 remotes (webpack built-in ModuleFederationPlugin) expect shareScope.pkg.version.get()
// to return a synchronous factory function. MF2's get() returns a Promise, which MF1
// can't resolve synchronously — the module ends up null, breaking hooks like useRef.
//
// This plugin pre-resolves already-loaded entries before MF2 calls container.init(),
// replacing async get() with synchronous equivalents so MF1 remotes share correctly.
//
// Why this app hits the race and contrast-ui-react doesn't:
// Every `<react-*>` directive created via createNgReactBridgeDirective (src/ng/react-bridge/index.ts)
// wraps its component in a provider stack that includes wrapWithSkeletorLocalizationProvider,
// which reads `contrastBase/state` (an MF1 remote). There are 150+ of these directives
// (org.ts, admin.ts, src/ng/components/**), and AngularJS mounts each one independently,
// whenever its own $compile/link cycle gets to that template — not on any schedule MF2 is
// aware of. Each mount is a separate, uncoordinated entry point into contrastBase's shared
// scope, so it's common for one directive to still be resolving a shared module's factory
// Promise (entry.loaded === true, get() not yet resolved) while another directive's render
// calls get() expecting a synchronous value. That's the race this plugin patches.
// contrast-ui-react has none of this: it's a single React root (mountHashApp/mountPathApp)
// with one continuous render tree and exactly one path through `contrastBase/state`, so
// there's no equivalent multi-entry-point race for it to hit. It's not that it avoids a
// specific remote — it just has no AngularJS scattering independent mounts across the page.
//
// Fixing this at the bridge level (e.g. a Suspense-like gate in the compose() chain in
// src/ng/react-bridge/index.ts) was considered and rejected: there's no documented MF
// runtime API for "is this shared module resolved yet" (this plugin already uses the
// closest thing — beforeInitContainer's shareScope internals), and gating all 150+ mount
// points would add latency and a new failure mode (unresolved remote → stuck spinner)
// without removing the underlying race. Patching the shared scope once, below the React
// layer, is simpler and has already been verified to fix the crash (see NS-2421).
//
// Verified: disabling this plugin's registration in webpack.config.js's runtimePlugins
// reliably reproduces `Uncaught TypeError: Cannot read properties of null (reading 'useRef')`
// inside <SkeletorLocalizationProvider> on a cold page load, caught by AngularErrorBoundary.
// Re-enabling it clears the error. If re-investigating "is this still needed," reproduce
// with that same test before removing it.
const mf1CompatPlugin = (): ModuleFederationRuntimePlugin => ({
  name: 'mf1-compat-plugin',
  async beforeInitContainer(args) {
    const { shareScope, remoteInfo } = args
    const resolutions: Promise<void>[] = []
    const log = (msg: string) =>
      console.debug(`[mf1-compat] ${remoteInfo?.name}: ${msg}`) // eslint-disable-line no-console

    log(
      `beforeInitContainer — scope keys: ${Object.keys(shareScope).join(', ')}`
    )

    for (const pkgName of Object.keys(shareScope)) {
      for (const version of Object.keys(shareScope[pkgName])) {
        const entry = shareScope[pkgName][version]
        const result = typeof entry.get === 'function' ? entry.get() : null
        const isAsync = result instanceof Promise
        if (result instanceof Promise) {
          result.catch(() => {})
        } // prevent unhandled rejection
        const resultType = result === null ? 'null' : typeof result
        const callResult =
          result !== null && result !== undefined && !isAsync
            ? (() => {
                try {
                  const r = (result as () => unknown)()
                  return r === null ? 'null' : typeof r
                } catch (e) {
                  return `call-err:${e}`
                }
              })()
            : 'n/a'
        log(
          `  ${pkgName}@${version}: loaded=${entry.loaded} async=${isAsync} getReturnType=${resultType} callResult=${callResult}`
        )

        if (entry.loaded && isAsync) {
          resolutions.push(
            (result as Promise<() => unknown>)
              .then((factory) => {
                shareScope[pkgName][version] = { ...entry, get: () => factory }
                log(`  ✓ patched ${pkgName}@${version} to sync`)
              })
              .catch((e) => {
                log(`  ✗ failed ${pkgName}@${version}: ${e}`)
              })
          )
        }
      }
    }

    await Promise.all(resolutions)
    log(`done — patched ${resolutions.length} entries`)
    return args
  }
})

// eslint-disable-next-line import/no-default-export
export default mf1CompatPlugin
